import { create } from 'zustand';
import type { BlogPostAdminDto, BlogPostInput, BlogPostStatus } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/** The store's blog (ADR-080) — posts searched and paged on the server, one open in its editor. */

interface BlogState {
  items: BlogPostAdminDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  status: BlogPostStatus | '';
  loading: boolean;
  error: string | null;

  current: BlogPostAdminDto | null;
  currentLoading: boolean;
  saving: boolean;
  saveError: string | null;

  load: (filters?: { search?: string; status?: BlogPostStatus | '' }) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<void>;
  clearCurrent: () => void;
  save: (id: string | null, input: BlogPostInput) => Promise<BlogPostAdminDto | null>;
  remove: (id: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;
const locale = () => useUiStore.getState().locale;

export const useBlogStore = create<BlogState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  status: '',
  loading: false,
  error: null,
  current: null,
  currentLoading: false,
  saving: false,
  saveError: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    const status = filters.status ?? get().status;
    set({ loading: true, error: null, search, status });
    try {
      const page = await withSession(() =>
        adminApi.blog.list({ limit: PAGE_SIZE, locale: locale(), ...(search.trim() ? { search: search.trim() } : {}), ...(status ? { status } : {}) }),
      );
      // A newer search answered first: this one is stale.
      if (get().search !== search || get().status !== status) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the posts.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, status, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.blog.list({
          limit: PAGE_SIZE,
          cursor: nextCursor,
          locale: locale(),
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(status ? { status } : {}),
        }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more posts.') });
    }
  },

  async open(id) {
    set({ currentLoading: true, saveError: null });
    try {
      const post = await withSession(() => adminApi.blog.get(id, locale()));
      set({ current: post, currentLoading: false });
    } catch (error) {
      set({ currentLoading: false, saveError: errorMessage(error, 'Could not load the post.') });
    }
  },

  clearCurrent() {
    set({ current: null, saveError: null });
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.blog.update(id, input, locale()) : adminApi.blog.create(input, locale())));
      set((state) => ({
        saving: false,
        current: saved,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [saved, ...state.items],
        total: id || state.total === null ? state.total : state.total + 1,
      }));
      return saved;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the post.') });
      return null;
    }
  },

  async remove(id) {
    set({ saving: true, saveError: null });
    try {
      await withSession(() => adminApi.blog.remove(id));
      set((state) => ({
        saving: false,
        current: null,
        items: state.items.filter((item) => item.id !== id),
        total: state.total === null ? null : state.total - 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not delete the post.') });
      return false;
    }
  },
}));
